import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  ClipboardList,
  ChevronRight,
  ChevronLeft,
  Check,
  Loader2,
} from "lucide-react";
import {
  invoicesApi,
  accountSummariesApi,
  billingTemplatesApi,
  clientsApi,
  mattersApi,
} from "../../../services/api";
import PremiumModal from "../../ui/PremiumModal";
import SearchableSelect from "../../ui/SearchableSelect";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CreateBillingDocumentModal: React.FC<Props> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);

  // Step 1: Type
  const [docType, setDocType] = useState<"invoice" | "account_summary">("invoice");

  // Step 2: Client + Scope
  const [clients, setClients] = useState<any[]>([]);
  const [matters, setMatters] = useState<any[]>([]);
  const [clientId, setClientId] = useState("");
  const [matterId, setMatterId] = useState("");
  const [scope, setScope] = useState<"client" | "matter">("client");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [paymentsMode, setPaymentsMode] = useState<"scope_only" | "all_client">("scope_only");

  // Step 3: Template
  const [templates, setTemplates] = useState<any[]>([]);
  const [templateId, setTemplateId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setDocType("invoice");
    setClientId("");
    setMatterId("");
    setScope("client");
    setDateFrom("");
    setDateTo("");
    setPaymentsMode("scope_only");
    setTemplateId(null);

    const loadData = async () => {
      try {
        const [cRes, mRes] = await Promise.all([
          clientsApi.list({ limit: 200 }),
          mattersApi.list({ limit: 200, status: "open" }),
        ]);
        setClients(cRes.data?.data || []);
        setMatters(mRes.data?.data || []);
      } catch (err) {
        console.error("Failed to load data:", err);
      }
    };
    loadData();
  }, [open]);

  useEffect(() => {
    if (step === 3) {
      billingTemplatesApi
        .list({ documentType: docType === "invoice" ? "invoice" : "account_summary" })
        .then((res) => {
          const d = res.data;
          setTemplates(Array.isArray(d) ? d : d?.data || []);
        })
        .catch(console.error);
    }
  }, [step, docType]);

  // When client changes and type is invoice, filter matters
  const clientMatters = matters.filter((m: any) => {
    const cid = typeof m.clientId === "object" ? m.clientId._id : m.clientId;
    return cid === clientId;
  });

  const handleCreate = async () => {
    setCreating(true);
    try {
      if (docType === "invoice") {
        // For invoice, create a draft with a placeholder line item and default due date
        const defaultDue = new Date();
        defaultDue.setDate(defaultDue.getDate() + 30);

        const res = await invoicesApi.create({
          matterId,
          clientId,
          lineItems: [
            {
              kind: "fee",
              description: "New line item",
              quantity: 1,
              unitPriceCents: 0,
              amountCents: 0,
              taxable: true,
              taxAmountCents: 0,
            },
          ],
          dueAt: defaultDue.toISOString().slice(0, 10),
          templateId: templateId || null,
          blockConfig: null,
        });
        onClose();
        navigate(`/billing/invoices/${res.data._id}/builder`);
      } else {
        const res = await accountSummariesApi.create({
          clientId,
          matterId: scope === "matter" ? matterId : null,
          scope,
          dateRange: {
            from: dateFrom || null,
            to: dateTo || null,
          },
          paymentsMode,
          templateId: templateId || null,
        });
        onClose();
        navigate(`/billing/statements/${res.data._id}/builder`);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to create document");
    } finally {
      setCreating(false);
    }
  };

  const canProceedStep2 = () => {
    if (docType === "invoice") return !!clientId && !!matterId;
    return !!clientId && (scope === "client" || !!matterId);
  };

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="Create Billing Document"
      icon={FileText}
      size="lg"
      footer={
        <div className="flex justify-between">
          <div>
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="inline-flex items-center gap-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 2 && !canProceedStep2()}
                className="inline-flex items-center gap-1 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handleCreate}
                disabled={creating}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50"
                style={{ backgroundColor: "var(--color-primary)" }}
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                {creating ? "Creating..." : "Create & Open Builder"}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {["Type", "Details", "Template"].map((label, i) => (
          <React.Fragment key={label}>
            {i > 0 && <div className="h-px flex-1 bg-slate-200" />}
            <div className="flex items-center gap-1.5">
              <div
                className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${
                  step > i + 1
                    ? "bg-brand-light text-brand"
                    : step === i + 1
                    ? "text-white"
                    : "bg-slate-100 text-slate-400"
                }`}
                style={step === i + 1 ? { backgroundColor: "var(--color-primary)" } : undefined}
              >
                {step > i + 1 ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </div>
              <span className={`text-xs font-medium ${step >= i + 1 ? "text-slate-700" : "text-slate-400"}`}>
                {label}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Type */}
      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">What type of billing document would you like to create?</p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { value: "invoice" as const, label: "Invoice", desc: "Bill for a single matter", icon: FileText },
              { value: "account_summary" as const, label: "Account Summary", desc: "Client-level statement across matters", icon: ClipboardList },
            ].map((opt) => {
              const OptIcon = opt.icon;
              return (
                <button
                  key={opt.value}
                  onClick={() => setDocType(opt.value)}
                  className={`p-5 rounded-xl border-2 text-left transition-all ${
                    docType === opt.value
                      ? "border-brand-accent bg-brand-light"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <OptIcon className={`h-6 w-6 mb-2 ${docType === opt.value ? "text-brand" : "text-slate-400"}`} />
                  <p className={`text-sm font-bold ${docType === opt.value ? "text-brand" : "text-slate-700"}`}>
                    {opt.label}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{opt.desc}</p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Step 2: Client + Scope */}
      {step === 2 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
            <SearchableSelect
              options={clients.map((c: any) => ({ value: c._id, label: c.displayName }))}
              value={clientId || null}
              onChange={(val) => {
                setClientId(val || "");
                setMatterId("");
              }}
              placeholder="Select client"
            />
          </div>

          {docType === "invoice" ? (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Matter *</label>
              <SearchableSelect
                options={clientMatters.map((m: any) => ({ value: m._id, label: `${m.matterNumber}  -  ${m.title}` }))}
                value={matterId || null}
                onChange={(val) => setMatterId(val || "")}
                placeholder={clientId ? "Select matter" : "Select client first"}
                disabled={!clientId}
              />
            </div>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Scope</label>
                <div className="flex gap-2">
                  {(["client", "matter"] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => {
                        setScope(s);
                        if (s === "client") setMatterId("");
                      }}
                      className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                        scope === s
                          ? "border-brand-accent bg-brand-light text-brand"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {s === "client" ? "All matters (client-wide)" : "Specific matter"}
                    </button>
                  ))}
                </div>
              </div>

              {scope === "matter" && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Matter *</label>
                  <SearchableSelect
                    options={clientMatters.map((m: any) => ({ value: m._id, label: `${m.matterNumber}  -  ${m.title}` }))}
                    value={matterId || null}
                    onChange={(val) => setMatterId(val || "")}
                    placeholder={clientId ? "Select matter" : "Select client first"}
                    disabled={!clientId}
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date From</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date To</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Payments Mode</label>
                <div className="flex gap-2">
                  {([
                    { value: "scope_only" as const, label: "Scope Only" },
                    { value: "all_client" as const, label: "All Client Payments" },
                  ]).map((pm) => (
                    <button
                      key={pm.value}
                      onClick={() => setPaymentsMode(pm.value)}
                      className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                        paymentsMode === pm.value
                          ? "border-brand-accent bg-brand-light text-brand"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {pm.label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Step 3: Template */}
      {step === 3 && (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Choose a template for your document layout.</p>
          <div className="grid grid-cols-2 gap-4">
            {/* No template option */}
            <button
              onClick={() => setTemplateId(null)}
              className={`p-5 rounded-xl border-2 text-left transition-all ${
                templateId === null
                  ? "border-brand-accent bg-brand-light"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <FileText className={`h-6 w-6 mb-2 ${templateId === null ? "text-brand" : "text-slate-400"}`} />
              <p className={`text-sm font-bold ${templateId === null ? "text-brand" : "text-slate-700"}`}>
                Default
              </p>
              <p className="text-xs text-slate-500 mt-1">Standard layout with default blocks</p>
            </button>

            {templates.map((t: any) => (
              <button
                key={t._id}
                onClick={() => setTemplateId(t._id)}
                className={`p-5 rounded-xl border-2 text-left transition-all ${
                  templateId === t._id
                    ? "border-brand-accent bg-brand-light"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div
                  className="h-6 w-6 rounded mb-2"
                  style={{ backgroundColor: t.theme?.primaryColor || "var(--color-primary)" }}
                />
                <p className={`text-sm font-bold ${templateId === t._id ? "text-brand" : "text-slate-700"}`}>
                  {t.name}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {t.description || (t.isSystem ? "System template" : "Custom template")}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}
    </PremiumModal>
  );
};

export default CreateBillingDocumentModal;
